import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useIntl } from 'react-intl'

import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'
import { ChooserDialog, includeInactiveParameter, type ChooserItem } from '@/shared/search'
import { TextField } from '@/shared/ui'

import { parseUserSummary, usersApi } from './user'

/** A person a chooser hands back: their User Profile's public id and display name. */
export interface ChosenUser {
  readonly id: string
  readonly name: string
}

interface UserChooserBase {
  readonly open: boolean
  readonly onClose: () => void
  /** The dialog's title; "Select User" by default. */
  readonly title?: string
  /** Lists only Active people, as for a choice that refuses a deactivated one. */
  readonly isActiveOnly?: boolean
  /** Lists deactivated people too, as for a merge; by default the API lists only active ones. */
  readonly isInactiveIncluded?: boolean
}

export type UserChooserProps = UserChooserBase &
  (
    | {
        readonly multi?: false
        /** The person picked, once the viewer confirms. */
        readonly onChoose: (user: ChosenUser) => void
        /** The person already chosen, shown picked once found. */
        readonly selectedId?: string | null
      }
    | {
        readonly multi: true
        /** The people picked, once the viewer confirms. */
        readonly onChoose: (users: readonly ChosenUser[]) => void
      }
  )

interface Results {
  readonly items: readonly ChooserItem[]
  readonly next: string | null
}

/**
 * "Select User", from the mockups' chooser: search the site's people by name or email, pick one
 * (or several with `multi`), then OK hands them back. Other features open it for their own
 * person fields.
 */
export function UserChooser(props: UserChooserProps) {
  const intl = useIntl()
  const client = useApiClient()
  const { open, onClose } = props
  const activeOnly = props.isActiveOnly === true
  const inactiveIncluded = props.isInactiveIncluded === true && !activeOnly
  const [text, setText] = useState('')
  const [results, setResults] = useState<Results | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [picked, setPicked] = useState<readonly ChooserItem[]>([])
  const request = useRef<AbortController | null>(null)

  // Starts clean each time it opens.
  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect -- resets the dialog as it opens
      setText('')
      setResults(null)
      setLoading(false)
      setFailed(false)
      setPicked([])
    }
    return () => request.current?.abort()
  }, [open])

  const load = useCallback(
    async (cursor: string | null) => {
      request.current?.abort()
      const controller = new AbortController()
      request.current = controller
      setLoading(true)
      setFailed(false)
      // A new search drops the last one's rows at once, so none that no longer match can be picked.
      if (cursor === null) {
        setResults(null)
      }
      // With no text the API lists the newest first; with text, by last name.
      const query = new URLSearchParams({ limit: '25' })
      if (text.trim() !== '') {
        query.set('q', text.trim())
      }
      if (activeOnly) {
        query.set('filter', 'status:in:active')
      }
      if (inactiveIncluded) {
        query.set(includeInactiveParameter, 'true')
      }
      if (cursor !== null) {
        query.set('cursor', cursor)
      }
      try {
        const page = parseCursorPage(await client.get(`${usersApi}?${query.toString()}`, controller.signal), parseUserSummary)
        // A user merged into another is found in Find Users but never chosen: the kept one is.
        const items = page.data
          .filter((user) => (user.mergedIntoId ?? null) === null)
          .map((user): ChooserItem =>
            user.email === null || user.email === undefined
              ? { id: user.id, label: user.displayName }
              : { id: user.id, label: user.displayName, sublabel: user.email },
          )
        setResults((current) => ({
          items: cursor === null || current === null ? items : [...current.items, ...items],
          next: page.pagination.nextCursor,
        }))
      } catch {
        // The client has logged it.
        if (!controller.signal.aborted) {
          setFailed(true)
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    },
    [activeOnly, inactiveIncluded, client, text],
  )

  const multi = props.multi === true
  const search = useCallback(() => void load(null), [load])
  const loadMore = useCallback(() => void load(results?.next ?? null), [load, results])
  const toggle = useCallback(
    (item: ChooserItem) =>
      setPicked((current) =>
        multi
          ? current.some((chosen) => chosen.id === item.id)
            ? current.filter((chosen) => chosen.id !== item.id)
            : [...current, item]
          : [item],
      ),
    [multi],
  )
  const confirm = useCallback(() => {
    // With nothing picked, a single chooser hands back the preselected person it shows as chosen.
    const preselected = multi ? [] : (results?.items.filter((item) => item.id === props.selectedId) ?? [])
    const chosen = (picked.length > 0 ? picked : preselected).map((item) => ({ id: item.id, name: item.label }))
    if (props.multi === true) {
      props.onChoose(chosen)
    } else if (chosen[0] !== undefined) {
      props.onChoose(chosen[0])
    }
    onClose()
  }, [picked, props, onClose, multi, results])
  const changeText = useCallback((event: ChangeEvent<HTMLInputElement>) => setText(event.target.value), [])
  const pickedIds = useMemo(() => picked.map((item) => item.id), [picked])
  // The preselected person counts as chosen only once a result shows them, so OK has someone to hand back.
  const selectedId = props.multi === true ? null : (props.selectedId ?? null)
  const preselectedShown = selectedId !== null && results?.items.some((item) => item.id === selectedId) === true ? selectedId : null

  const shared = {
    open,
    onClose,
    title: props.title ?? intl.formatMessage({ id: 'siteManagement.users.chooser.title' }),
    filters: (
      <TextField
        label={intl.formatMessage({ id: 'siteManagement.users.chooser.label' })}
        placeholder={intl.formatMessage({ id: 'siteManagement.users.chooser.placeholder' })}
        value={text}
        onChange={changeText}
      />
    ),
    onSearch: search,
    results: results?.items ?? null,
    loading,
    failed,
    hasMore: results !== null && results.next !== null,
    onLoadMore: loadMore,
    onToggle: toggle,
    onConfirm: confirm,
  }

  return props.multi === true ? (
    <ChooserDialog {...shared} multi selected={pickedIds} />
  ) : (
    <ChooserDialog {...shared} selected={picked[0]?.id ?? preselectedShown} />
  )
}
